import type { RosterUploadFile } from '../../../types/adminRosterUploads';
import { formatInstant } from './rosterUploadFormat';
import { AdminPanel } from '../AdminPage';

interface RosterUploadFilesProps {
  files: readonly RosterUploadFile[];
  signedUrlExpiresAt: string | null;
}

const FileCard = ({ file }: { file: RosterUploadFile }) => {
  if (!file.signedUrl) {
    return (
      <li className="roster-upload__file roster-upload__file--unavailable">
        <span className="roster-upload__file-name">{file.fileName}</span>
        <span className="roster-upload__muted">This file could not be linked for viewing.</span>
      </li>
    );
  }

  return (
    <li className="roster-upload__file">
      <a
        className="roster-upload__file-link"
        href={file.signedUrl}
        target="_blank"
        rel="noopener noreferrer"
      >
        {file.isImage && (
          <img
            className="roster-upload__thumbnail"
            src={file.signedUrl}
            // Decorative: the file name beside it is already the link text, so a descriptive alt
            // would announce the name twice.
            alt=""
            loading="lazy"
          />
        )}
        <span className="roster-upload__file-name">{file.fileName}</span>
        <span className="roster-upload__file-action">Open in new tab</span>
      </a>
    </li>
  );
};

/**
 * The upload's source files. The links are short-lived signed URLs, so the expiry is stated: an
 * admin who comes back to an open tab later needs to know to reload rather than assume the file is
 * gone.
 */
export const RosterUploadFiles = ({ files, signedUrlExpiresAt }: RosterUploadFilesProps) => (
  <AdminPanel title={`Files (${files.length})`} titleId="roster-upload-files-title">
    {files.length === 0 ? (
      <p className="admin-state">No files were recorded for this upload.</p>
    ) : (
      <>
        {signedUrlExpiresAt && (
          <p className="roster-upload__muted">
            Links expire at {formatInstant(signedUrlExpiresAt)}. Reload the page for fresh ones.
          </p>
        )}
        <ul className="roster-upload__files" data-testid="roster-upload-files">
          {files.map((file, index) => (
            <FileCard key={`${index}-${file.url}`} file={file} />
          ))}
        </ul>
      </>
    )}
  </AdminPanel>
);
